import {
    AuthenticationProvider, AuthenticationProviderOptions,
    Client as GraphClient, ClientOptions,
} from "@microsoft/microsoft-graph-client";
import ApiService from "../../components/ApiService";
import { UserAccessLevel } from "../../models/UserAccessRecord";
import {
    AuthenticationResult, EndSessionRequest,
    PopupRequest,
    SilentRequest
} from "@azure/msal-browser";
import * as msal from "@azure/msal-browser";
import {AuthState, IRootState} from "../../store";
import {AccountInfo} from "@azure/msal-common";

const CDB_OAUTH_SCOPES = [
    `api://${process.env.REACT_APP_AUTH_CLIENT_ID}/User.SignIn`
];

const MS_OAUTH_SCOPES = [
    'openid',
    'profile',
    'https://graph.microsoft.com/.default'
];

const REDIRECT_URI = `https://${window.location.host}/authentication/login-callback`;

const msalInstance = new msal.PublicClientApplication(
    {
        auth: {
            clientId: process.env.REACT_APP_AUTH_CLIENT_ID!,
        },
        cache: {
            cacheLocation: "localStorage",
        },
    }
);

export const getTokenSilentFallbackRedirect = (scopes: string[]): Promise<AuthenticationResult | void> => {
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount) {
        throw "No active account";
    }

    const silentReq: SilentRequest = {
        account: activeAccount,
        scopes: scopes
    };
    
    return msalInstance.acquireTokenSilent(silentReq).catch((e) =>
    {
        return msalInstance.acquireTokenRedirect({
            account: activeAccount,
            scopes: scopes,
            redirectUri: REDIRECT_URI
        })
    });
};

const setUser = (state: AuthState,user?: AccountInfo): AuthState =>
{
    if(user)
    {
        const userIsCdbEmployee = user.tenantId === process.env.REACT_APP_CDB_TENANT_ID;
        state.msalInstance.setActiveAccount(user);
        return Object.assign({}, state, {user: user, userIsCdbEmployee : userIsCdbEmployee})
    }
    else
    {
        return Object.assign({}, state, {user: undefined, userIsCdbEmployee : false})
    }
}

const authReducer = (state: AuthState | undefined, action: { type: string, user: AccountInfo, userAccesLevel?: UserAccessLevel,loginRequired: boolean}
) => {
    
    if (state === undefined) {
        
        const authProvider: AuthenticationProvider = {
            
            async getAccessToken(opts?: AuthenticationProviderOptions): Promise<string> {

                return getTokenSilentFallbackRedirect(MS_OAUTH_SCOPES).then(authResult => {
                    return authResult ? authResult.accessToken : "";
                });
            }
        };
        const clientOptions: ClientOptions = {
            authProvider: authProvider
        };
        const graphClient = GraphClient.initWithMiddleware(clientOptions);

        const apiService = new ApiService({
            async getAccessToken(): Promise<string> {
                return getTokenSilentFallbackRedirect(CDB_OAUTH_SCOPES).then(authResult => {
                    return authResult ? authResult.accessToken : "";
                })
            }
        })

        state = {
            msalInstance: msalInstance,
            user: undefined,
            userIsCdbEmployee: false,
            usersCdbAccessLevel: undefined,
            graphClient,
            apiService,
        };

        const user = msalInstance.getAllAccounts()[0];
        if(user)
        {
            state = setUser(state,user);
        }
    }
    
    switch (action.type) {
        
        case AuthActions.SET_USER:
            return setUser(state,action.user);

        case AuthActions.ADMIN_SET_USER_ACCESS_LEVEL:
            return Object.assign({}, state, { usersCdbAccessLevel: action.userAccesLevel });
        default:
            return state;
    }
};

export default authReducer;

export class AuthActions
{
    public static readonly SET_USER = "SET_USER";

    public static readonly ADMIN_SET_USER_ACCESS_LEVEL = "ADMIN_SET_USER_ACCESS_LEVEL";

    public static signIn(thenRedirectTo?: string) {
        return async function (dispatch: Function, getState: Function) {
            let state: IRootState = getState();

            const req: PopupRequest = {
                scopes: MS_OAUTH_SCOPES,
                redirectUri: REDIRECT_URI,
            };
            
            await state.auth.msalInstance.loginPopup(req).then(popupResult => {
                
                
                if(popupResult)
                {
                    dispatch({
                        type: AuthActions.SET_USER,
                        user: state.auth.msalInstance.getAllAccounts()[0]
                    });
                    
                    if(thenRedirectTo)
                    {
                        state.history.push(thenRedirectTo);
                    }
                }

            }).catch(popupError => {
                console.log("popupError",popupError);
            });
        }
    }
    
    public static signOut() {
        return async function (dispatch: Function, getState: Function) {
            const state: IRootState = getState();

            const req: EndSessionRequest = {
                account: state.auth.user,
                postLogoutRedirectUri: `https://${window.location.host}/`,
            };
            
            await state.auth.msalInstance.logoutRedirect(req);
        }
    }

    public static getCdbUserAccessLevel(forceReload: boolean = false) {
        return async function (dispatch: Function, getState: Function) {
            const state: IRootState = getState();

            if (!state.auth.userIsCdbEmployee) {
                return;
            }

            if (!forceReload && state.auth.usersCdbAccessLevel != undefined) {
                return; // Already loaded
            }

            const userAccesLevel = await state.auth.apiService.getCurrentUserAccess()

            dispatch({
                type: AuthActions.ADMIN_SET_USER_ACCESS_LEVEL,
                userAccesLevel
            });
        };
    }
    
}